import { useMemo, useState } from 'react'
import type { Garment, SessionCommand } from '@fitting-room/contracts'
import { IconGlyph } from '../../icons/registry'
import { bestImage } from '../../lib/media'
import { uiStrings } from '../../lib/strings'
import { text } from '../../lib/text'
import { OptionPlate } from '../../components/OptionPlate'
import { StepShell } from '../../components/RoomChrome'
import { exemplarsFor } from '../exemplars'
import { suggestedOptionIds } from '../suggestions'
import { useGarmentPage } from '../useSession'
import type { StepProps } from '../types'
import { usePendingExit } from '../usePendingExit'

/**
 * Illustrated choice tiles. Selection is expressed by the tile lifting and its
 * outline turning primary — there is no tick, because a tick reads as a form
 * and this is meant to read as pointing at something in a room.
 *
 * `presentation: 'plates'` asks the same question with photographs instead:
 * same roles, same states, same suggestion label, no frame. `presentation:
 * 'list'` asks it as one option per row with no picture at all, for options
 * that are words nothing can illustrate. Which questions are worth which is a
 * judgement about the words on them, so it is authored in the step document
 * rather than decided here.
 *
 * Nothing here knows what is being asked. Labels, captions, icons and the
 * answer→attribute effects all come from the step document.
 */
export function QuestionStep({ step, ctx }: StepProps<'question'>) {
  const { locale, view } = ctx
  const t = uiStrings(locale)
  const plates = step.presentation === 'plates'
  const list = step.presentation === 'list'

  const single = step.select === 'one'
  const maxSelect = single ? 1 : (step.maxSelect ?? step.options.length)

  const suggested = useMemo(
    () => suggestedOptionIds(view.flow, view.session.answers.choices, step.id),
    [view.flow, view.session.answers.choices, step.id],
  )

  /**
   * Whether she has touched this screen at all.
   *
   * The seed below fills the tiles from a SUGGESTION, and a suggestion is not
   * an answer. Without this, walking into the step from the deck and straight
   * back out would write the suggestion down as her own choice.
   */
  const [touched, setTouched] = useState(false)

  const [selected, setSelected] = useState<readonly string[]>(() => {
    const answered = view.session.answers.choices[step.id]
    // An empty array is an answer — she skipped this screen, or cleared it —
    // so only the absence of one leaves room for the suggestion to seed.
    if (answered) return answered
    // Taken in the screen's own order and never past the limit: a seed that
    // arrives over it would open the step with the rest already disabled.
    return step.options
      .filter((o) => suggested.has(o.id))
      .slice(0, maxSelect)
      .map((o) => o.id)
  })

  const toggle = (optionId: string) => {
    setTouched(true)
    setSelected((current) => {
      if (single) return current[0] === optionId ? [] : [optionId]
      if (current.includes(optionId)) return current.filter((id) => id !== optionId)
      if (current.length >= maxSelect) return current
      return [...current, optionId]
    })
  }

  const answer = (optionIds: readonly string[]): SessionCommand => ({
    type: 'answer',
    stepId: step.id,
    optionIds: [...optionIds],
  })

  const canContinue = selected.length > 0 || step.skippable

  // Leaving sideways commits exactly what Continue would have.
  usePendingExit(ctx, touched ? () => [answer(selected)] : null)

  // Drawn shapes are tall and narrow, so three to a row on a 390px screen reads
  // as a set — which is how `ref/design/before-the-hour/Quiz.dc.html` draws it.
  // Words with nothing drawn beside them need the width instead. Plates carry
  // a photograph and a word under it, so they take half the screen each and a
  // taller gutter.
  // A `list` step goes the whole way: one column, and no gutter at all,
  // because the rows carry hairlines on their joins and a gap would break the
  // run of them back into a stack of separate things.
  const illustrated = step.options.some((o) => o.icon)
  const grid = plates
    ? 'grid grid-cols-2 gap-x-4 gap-y-7'
    : list
      ? 'grid grid-cols-1'
      : `grid gap-3 ${illustrated ? 'grid-cols-3' : 'grid-cols-2'}`

  /*
   * An opt-out is an effect-free option ON A SCREEN THAT HAS OTHERS — "not
   * sure" among five body shapes. A question where nothing weighs, like
   * shopping stage, is five real answers rather than five escape hatches, and
   * drawing them all dashed would say the opposite.
   */
  const hasWeighted = step.options.some((o) => o.effects.length > 0)

  /*
   * The pictures, resolved once for the whole screen.
   *
   * The source is the deep page rather than the deck. Its key carries no
   * ranking signature, so every screen that wants to look something up in the
   * catalogue reads the one cached page instead of fetching a copy of its own,
   * and only a plate screen asks for it at all.
   *
   * Which item illustrates which word is then settled inside `exemplarFor`,
   * off the ids and never off a score: that page is ranked, its order moves
   * with every answer, and a word whose picture changed because she edited an
   * earlier screen would read as broken.
   */
  const page = useGarmentPage(ctx.token, plates)
  const exemplars = useMemo(() => {
    if (!plates) return new Map<string, Garment>()
    return exemplarsFor(step.options, (page.data?.items ?? []).map((item) => item.garment))
  }, [plates, page.data, step.options])

  return (
    <StepShell
      title={text(step.title, locale)}
      body={text(step.body, locale)}
      progress={ctx.progress}
      locale={locale}
      skip={
        step.skippable
          ? { label: t.skipThis, onSkip: () => ctx.advance([answer([])]), disabled: ctx.pending }
          : undefined
      }
      footer={
        <div className="pb-2" style={{ background: 'linear-gradient(to top, var(--fr-ground-deep) 62%, transparent)' }}>
          {!single && step.maxSelect ? (
            <p className="fr-eyebrow mb-3 text-center">{t.selectUpTo(step.maxSelect)}</p>
          ) : null}
          <button
            type="button"
            className="fr-btn fr-btn-primary w-full"
            disabled={ctx.pending || !canContinue}
            onClick={() => ctx.advance([answer(selected)])}
          >
            {t.continue}
          </button>
        </div>
      }
    >
      <div
        className={grid}
        role={single ? 'radiogroup' : 'group'}
        aria-label={text(step.title, locale) || step.id}
      >
        {step.options.map((option) => {
          const label = text(option.label, locale)
          const isSelected = selected.includes(option.id)
          const atLimit = !single && !isSelected && selected.length >= maxSelect
          const optOut = hasWeighted && option.effects.length === 0
          const suggestionLabel = suggested.has(option.id) ? t.suggested : undefined

          if (plates) {
            // Only the source travels. The exemplar's own alt text describes
            // a thing in the catalogue, and what she is choosing here is the
            // word — so the picture goes in decorative and the label is the
            // accessible name.
            const exemplar = exemplars.get(option.id)
            const image = exemplar ? bestImage(exemplar.media) : null
            return (
              <OptionPlate
                key={option.id}
                label={label}
                caption={option.caption ? text(option.caption, locale) : undefined}
                icon={option.icon}
                imageSrc={image?.src}
                selected={isSelected}
                suggestionLabel={suggestionLabel}
                single={single}
                optOut={optOut}
                disabled={atLimit}
                onSelect={() => toggle(option.id)}
              />
            )
          }

          if (list) {
            // No icon and no space held for one: these are options that
            // nothing illustrates, and a row with an empty square at the head
            // of it is a worse answer than the grid this replaced.
            return (
              <button
                key={option.id}
                type="button"
                className="fr-choice-row"
                data-optout={optOut ? true : undefined}
                role={single ? 'radio' : undefined}
                aria-checked={single ? isSelected : undefined}
                aria-pressed={single ? undefined : isSelected}
                data-selected={isSelected}
                disabled={atLimit}
                onClick={() => toggle(option.id)}
              >
                <span className="fr-display text-[1.125rem] leading-tight">{label}</span>
                {option.caption ? (
                  <span className="text-[0.8125rem] leading-snug" style={{ color: 'var(--fr-ink-faint)' }}>
                    {text(option.caption, locale)}
                  </span>
                ) : null}
                {suggestionLabel ? (
                  // Where the idea came from, not what is chosen — same as
                  // on a tile, and it must be here even though no list step
                  // seeds one yet: a presentation that quietly drops a state
                  // is a trap for whoever authors the next flow. The eyebrow
                  // keeps its own tracking, unlike the tile's, because a row
                  // has the whole column to set the word in.
                  <span className="fr-eyebrow" style={{ color: 'var(--fr-primary)' }}>
                    {suggestionLabel}
                  </span>
                ) : null}
              </button>
            )
          }

          return (
            <button
              key={option.id}
              type="button"
              className="fr-tile"
              data-optout={optOut ? true : undefined}
              role={single ? 'radio' : undefined}
              aria-checked={single ? isSelected : undefined}
              aria-pressed={single ? undefined : isSelected}
              data-selected={isSelected}
              disabled={atLimit}
              style={atLimit ? { opacity: 0.4 } : undefined}
              onClick={() => toggle(option.id)}
            >
              <IconGlyph name={option.icon} label={label} size={46} />
              <span className="fr-display text-[1.125rem] leading-tight">{label}</span>
              {option.caption ? (
                <span className="text-[0.8125rem] leading-snug" style={{ color: 'var(--fr-ink-faint)' }}>
                  {text(option.caption, locale)}
                </span>
              ) : null}
              {suggestionLabel ? (
                // Where the idea came from, not what is chosen: it stays when
                // she takes the tile off. The eyebrow's tracking is drawn in
                // because the word has a third of a phone's width to fit in.
                <span className="fr-eyebrow" style={{ color: 'var(--fr-primary)', letterSpacing: '0.12em' }}>
                  {suggestionLabel}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>
    </StepShell>
  )
}
